import type { AudioDirector } from '@src/audio/AudioDirector';
import { SfxId } from '@src/audio/Sfx';

export type PauseAction = 'resume' | 'restart' | 'title';

export interface PauseMenuOptions {
  host: HTMLElement;
  audio: AudioDirector;
  /** Freeze sim + duck audio before the overlay appears. */
  pauseWorld: () => void;
  /** Unfreeze sim + restore audio after Resume. */
  resumeWorld: () => void;
  onRestart: () => void;
  onTitle: () => void;
}

const STICK_DEADZONE = 0.55;

interface PauseOption {
  id: PauseAction | 'mute';
  label: string;
  hint: string;
}

const OPTIONS: readonly PauseOption[] = [
  { id: 'resume', label: 'RESUME', hint: 'Esc / P / B' },
  { id: 'restart', label: 'RESTART RUN', hint: 'Room 1' },
  { id: 'title', label: 'TITLE SCREEN', hint: 'Choose tier' },
  { id: 'mute', label: 'SOUND: ON', hint: 'Toggle' },
];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

/**
 * In-run pause overlay. Keyboard (arrows/WASD, Enter/Space, Esc/P), mouse, and
 * gamepad (d-pad/stick, A select, B/Start resume). Armed only after the arena
 * handoff so Escape during the cold open stays a skip.
 */
export class PauseMenu {
  private readonly root: HTMLDivElement;
  private readonly buttons: HTMLButtonElement[] = [];
  private readonly muteLabel: HTMLSpanElement;
  private readonly audio: AudioDirector;
  private readonly pauseWorld: () => void;
  private readonly resumeWorld: () => void;
  private readonly onRestart: () => void;
  private readonly onTitle: () => void;
  private shown = false;
  private armed = false;
  private selected = 0;
  private muted = false;
  private padButtons: boolean[] = [];
  private padAxisY = 0;
  private raf = 0;

  constructor(opts: PauseMenuOptions) {
    this.audio = opts.audio;
    this.pauseWorld = opts.pauseWorld;
    this.resumeWorld = opts.resumeWorld;
    this.onRestart = opts.onRestart;
    this.onTitle = opts.onTitle;

    this.root = el('div', 'pause-menu', opts.host);
    this.root.setAttribute('aria-hidden', 'true');
    el('div', 'pause-veil', this.root);
    const panel = el('div', 'pause-panel', this.root);
    el('p', 'pause-kicker', panel, 'ARCANE BROADCAST');
    el('h1', 'pause-title', panel, 'PAUSED');
    el('p', 'pause-sub', panel, 'The arena holds. Choose your next move.');
    const list = el('div', 'pause-list', panel);

    let muteLabel: HTMLSpanElement | null = null;
    for (const opt of OPTIONS) {
      const btn = el('button', 'pause-opt', list);
      btn.type = 'button';
      btn.dataset.id = opt.id;
      const label = el('span', 'pause-opt-label', btn, opt.label);
      if (opt.id === 'mute') muteLabel = label;
      el('span', 'pause-opt-hint', btn, opt.hint);
      btn.addEventListener('pointerenter', () => {
        if (!this.shown) return;
        this.select(OPTIONS.findIndex((o) => o.id === opt.id), false);
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.shown || e.detail === 0) return;
        this.activate(opt.id);
      });
      this.buttons.push(btn);
    }
    this.muteLabel = muteLabel!;
    el('p', 'pause-controls', panel, 'Up / Down or stick  ·  Enter / A to select  ·  Esc / P / B to resume');
    this.renderSelection();
  }

  /** Allow Escape / P / Start to open the menu (after the arena begins). */
  arm(): void {
    if (this.armed) return;
    this.armed = true;
    window.addEventListener('keydown', this.onKey);
    this.padButtons = [];
    this.padAxisY = 0;
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(this.frame);
  }

  disarm(): void {
    this.armed = false;
    this.closeOverlay();
    window.removeEventListener('keydown', this.onKey);
    cancelAnimationFrame(this.raf);
  }

  isOpen(): boolean {
    return this.shown;
  }

  /** Open the pause menu and freeze the world. No-op if already open or disarmed. */
  open(): void {
    if (!this.armed || this.shown) return;
    this.pauseWorld();
    this.shown = true;
    this.selected = 0;
    this.renderSelection();
    this.root.classList.add('show');
    this.root.setAttribute('aria-hidden', 'false');
    this.audio.sfx.play(SfxId.MenuMove, 0.7);
  }

  destroy(): void {
    this.disarm();
    this.root.remove();
  }

  private closeOverlay(): void {
    if (!this.shown) return;
    this.shown = false;
    this.root.classList.remove('show');
    this.root.setAttribute('aria-hidden', 'true');
  }

  private readonly frame = (): void => {
    if (!this.armed) return;
    this.pollGamepad();
    this.raf = requestAnimationFrame(this.frame);
  };

  private readonly onKey = (e: KeyboardEvent): void => {
    if (!this.armed || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.code.startsWith('F') && e.code.length <= 3) return;

    if (!this.shown) {
      if (e.code === 'Escape' || e.code === 'KeyP') {
        e.preventDefault();
        this.open();
      }
      return;
    }

    switch (e.code) {
      case 'Escape':
      case 'KeyP':
        e.preventDefault();
        this.activate('resume');
        break;
      case 'ArrowUp':
      case 'KeyW':
        e.preventDefault();
        this.move(-1);
        break;
      case 'ArrowDown':
      case 'KeyS':
        e.preventDefault();
        this.move(1);
        break;
      case 'Enter':
      case 'Space':
      case 'NumpadEnter':
        e.preventDefault();
        this.activate(OPTIONS[this.selected].id);
        break;
      default:
        break;
    }
  };

  private move(delta: number): void {
    const n = OPTIONS.length;
    this.select((this.selected + delta + n) % n, true);
  }

  private select(index: number, viaInput: boolean): void {
    if (index < 0 || index >= OPTIONS.length || index === this.selected) return;
    this.selected = index;
    this.renderSelection();
    if (viaInput) this.audio.sfx.play(SfxId.MenuMove, 0.75);
  }

  private renderSelection(): void {
    this.buttons.forEach((btn, i) => {
      btn.classList.toggle('selected', i === this.selected);
    });
    this.muteLabel.textContent = this.muted ? 'SOUND: OFF' : 'SOUND: ON';
  }

  private activate(id: PauseAction | 'mute'): void {
    if (id === 'mute') {
      this.muted = !this.muted;
      this.audio.setMuted(this.muted);
      this.renderSelection();
      if (!this.muted) this.audio.sfx.play(SfxId.MenuMove, 0.7);
      return;
    }
    this.audio.sfx.play(SfxId.MenuConfirm, 0.85);
    this.closeOverlay();
    if (id === 'resume') this.resumeWorld();
    else if (id === 'restart') this.onRestart();
    else this.onTitle();
  }

  private pollGamepad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let pad: Gamepad | null = null;
    for (const p of pads) {
      if (p?.connected) {
        pad = p;
        break;
      }
    }
    if (!pad) {
      this.padButtons = [];
      this.padAxisY = 0;
      return;
    }
    const pressed = (i: number): boolean => !!pad.buttons[i]?.pressed;
    const edge = (i: number): boolean => pressed(i) && !this.padButtons[i];
    const ay = pad.axes[1] ?? 0;
    const stepY = Math.abs(ay) > STICK_DEADZONE ? Math.sign(ay) : 0;
    const stickMove = stepY !== 0 && stepY !== this.padAxisY ? stepY : 0;

    if (!this.shown) {
      // Start / Options (standard button 9)
      if (edge(9)) this.open();
    } else {
      if (edge(12) || stickMove < 0) this.move(-1);
      if (edge(13) || stickMove > 0) this.move(1);
      if (edge(0)) this.activate(OPTIONS[this.selected].id);
      if (edge(1) || edge(9)) this.activate('resume');
    }

    this.padButtons = pad.buttons.map((b) => b.pressed);
    this.padAxisY = stepY;
  }
}
